import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { of, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Preferences } from '@capacitor/preferences';
import { StateService } from '../../services/state.service';
import { ToolService } from '../../services/tool.service';
import { Company, UserType } from '../../api/models';
import { HelpComponent } from '../help/help.component';
import { CompanyFormComponent } from '../company-form/company-form.component';

@Component({
  selector: 'lib-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss'],
  standalone: false,
})
export class SettingsComponent implements OnInit, OnDestroy {
  private translate = inject(TranslateService);

  constructor(
    public state: StateService,
    public tools: ToolService,
    private modalController: ModalController
  ) {
    this.currentLanguage = this.translate.currentLang() || 'de';
  }

  private subs: Subscription[] = [];
  nodeItemsStorageInGb: string;
  selectedCompany: Company;
  public companyLogoUrl: string | null = null;
  public currentLanguage: string = 'de';

  ngOnInit() {
    this.currentLanguage = this.translate.currentLang() || 'de';
    this.state.loadSignature();
    this.nodeItemsStorageInGb = this.state.calculateStorageSize();
    this.subs.push(
      this.state.selectedCompany$
        .pipe(
          switchMap((company) => {
            this.selectedCompany = company;
            return company?.id
              ? this.state.downloadPreviewImage(company.id)
              : of(null);
          })
        )
        .subscribe((image) => this.updateCompanyLogo(image))
    );
  }

  async changeLanguage(lang: string) {
    if (!lang) return;
    this.currentLanguage = lang;
    this.translate.use(lang);
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('lang', lang);
    }
    await Preferences.set({ key: 'lang', value: lang }).catch(() => {});
  }

  ngOnDestroy() {
    this.subs?.forEach((x) => x.unsubscribe());
    this.releaseCompanyLogoUrl();
  }

  private updateCompanyLogo(image: Blob | null) {
    this.releaseCompanyLogoUrl();
    this.companyLogoUrl = image ? URL.createObjectURL(image) : null;
  }

  private releaseCompanyLogoUrl() {
    if (this.companyLogoUrl) {
      URL.revokeObjectURL(this.companyLogoUrl);
      this.companyLogoUrl = null;
    }
  }

  saveSignature(text: string) {
    this.state.saveSignature(text);
  }

  getUserType(): string {
    if (this.state.userType == UserType.$1)
      return this.translate.instant('user.type.office');
    if (this.state.userType == UserType.$2)
      return this.translate.instant('user.type.companyAdmin');
    if (this.state.userType == UserType.$3)
      return this.translate.instant('user.type.appAdmin');
    return this.translate.instant('user.type.employee');
  }

  itemChanged(company: Company) {
    this.state.changeSelectedCompany(company);
  }

  async showHelpModal() {
    const modal = await this.modalController.create({
      component: HelpComponent,
    });
    modal.present();
  }

  async createCompany() {
    const modal = await this.modalController.create({
      component: CompanyFormComponent,
    });
    modal.present();
  }

  async editCompany() {
    const modal = await this.modalController.create({
      component: CompanyFormComponent,
      componentProps: {
        companyId: this.selectedCompany.id,
      },
    });
    modal.present();
  }
}
